/* Uniq Socks — Premium Welcome Popup
 * Klaviyo is the backend: this script talks only to Klaviyo's PUBLIC
 * client-side API (company id, no secret). The existing Klaviyo welcome
 * flow is solely responsible for generating, assigning, and emailing the
 * unique 10% code — this popup never fetches, displays, or stores a
 * coupon code itself.
 */
(function () {
  "use strict";

  var STORAGE_KEY = "uwp_state_v1"; // { dismissedAt, subscribedAt }
  var OLD_POPUP_FLAG = "uwp_active"; // sessionStorage flag other scripts can check

  var overlay = document.querySelector("[data-uwp-overlay]");
  if (!overlay) return;

  var delayMs = parseInt(overlay.getAttribute("data-delay-ms"), 10) || 4000;
  var suppressDays = parseInt(overlay.getAttribute("data-suppress-days"), 10) || 5;
  var listId = overlay.getAttribute("data-list-id");
  var companyId = overlay.getAttribute("data-klaviyo-company-id");

  var content = overlay.querySelector(".uwp-content");
  var form = overlay.querySelector("[data-uwp-form]");
  var emailInput = overlay.querySelector("[data-uwp-email]");
  var errorEl = overlay.querySelector("[data-uwp-error]");
  var submitBtn = overlay.querySelector("[data-uwp-submit]");
  var submitLabel = overlay.querySelector("[data-uwp-submit-label]");
  var shopBtn = overlay.querySelector("[data-uwp-shop]");
  var heroImg = overlay.querySelector("[data-uwp-hero]");
  var closeEls = overlay.querySelectorAll("[data-uwp-close]");

  // ---------- Suppression: don't show to recent dismissers/subscribers ----------
  function readState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
    catch (e) { return {}; }
  }
  function writeState(patch) {
    var s = readState();
    for (var k in patch) s[k] = patch[k];
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (e) {}
  }
  function daysSince(ts) {
    if (!ts) return Infinity;
    return (Date.now() - ts) / (1000 * 60 * 60 * 24);
  }

  var state = readState();
  var alreadySubscribed = !!state.subscribedAt;
  var recentlyDismissed = daysSince(state.dismissedAt) < suppressDays;

  if (alreadySubscribed || recentlyDismissed) {
    return; // do not schedule the popup at all
  }

  // ---------- Image readiness ----------
  function imageReady() {
    if (!heroImg) return Promise.resolve();
    if (heroImg.complete && heroImg.naturalWidth > 0) return Promise.resolve();
    if (heroImg.decode) {
      return heroImg.decode().catch(function () {
        // decode() can reject in some browsers even on eventual success;
        // fall back to the load event instead of blocking forever.
        return new Promise(function (resolve) {
          heroImg.addEventListener("load", resolve, { once: true });
          heroImg.addEventListener("error", resolve, { once: true });
        });
      });
    }
    return new Promise(function (resolve) {
      heroImg.addEventListener("load", resolve, { once: true });
      heroImg.addEventListener("error", resolve, { once: true });
    });
  }

  var timerElapsed = new Promise(function (resolve) {
    setTimeout(resolve, delayMs);
  });

  Promise.all([imageReady(), timerElapsed]).then(openPopup);

  // ---------- Open / close ----------
  var lastFocused = null;
  var KEYBOARD_FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function openPopup() {
    if (document.hidden) {
      document.addEventListener("visibilitychange", function onVis() {
        if (!document.hidden) {
          document.removeEventListener("visibilitychange", onVis);
          openPopup();
        }
      });
      return;
    }
    lastFocused = document.activeElement;
    overlay.setAttribute("data-uwp-visible", "true");
    try { sessionStorage.setItem(OLD_POPUP_FLAG, "1"); } catch (e) {}
    document.addEventListener("keydown", onKeydown);
    var firstField = emailInput || overlay.querySelector(KEYBOARD_FOCUSABLE);
    if (firstField) firstField.focus();
  }

  function closePopup(reason) {
    overlay.setAttribute("data-uwp-closing", "true");
    document.removeEventListener("keydown", onKeydown);
    setTimeout(function () {
      overlay.setAttribute("data-uwp-visible", "false");
      overlay.removeAttribute("data-uwp-closing");
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }, 320);
    if (reason === "dismiss") writeState({ dismissedAt: Date.now() });
  }

  function onKeydown(e) {
    if (e.key === "Escape") {
      closePopup("dismiss");
      return;
    }
    if (e.key === "Tab") {
      var focusable = overlay.querySelectorAll(KEYBOARD_FOCUSABLE);
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }

  closeEls.forEach(function (el) {
    el.addEventListener("click", function () { closePopup("dismiss"); });
  });
  overlay.addEventListener("click", function (e) {
    if (e.target === overlay) closePopup("dismiss");
  });

  // ---------- Email validation ----------
  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  // ---------- Klaviyo public subscribe (client-side API, public company id only) ----------
  // This is the entire integration: subscribe the profile to the existing
  // list, which triggers the existing Klaviyo welcome flow. That flow —
  // unchanged — is solely responsible for generating, assigning, and
  // emailing the unique 10% code.
  //
  // NOTE: the "subscriptions" object was previously nested under
  // profile.data.attributes to explicitly set marketing consent. Klaviyo's
  // client subscribe endpoint (revision 2024-10-15) rejects that field on
  // the profile resource with a 400 "'subscriptions' is not a valid field
  // for the resource 'profile'" error — this was the actual bug preventing
  // every popup signup from reaching Klaviyo. Consent is implicit
  // (SUBSCRIBED) for profiles submitted through this endpoint via the list
  // relationship, so the field must be omitted entirely.
  function klaviyoSubscribe(email) {
    // TEMP DIAGNOSTIC (test theme only, remove once verified in production):
    // confirms the request is actually being sent, with no sensitive data.
    if (window.console && console.log) {
      console.log("[UWP] Klaviyo subscribe request →", { company_id: companyId, list_id: listId });
    }
    return fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + encodeURIComponent(companyId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: "2024-10-15"
      },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            list: { data: { type: "list", id: listId } }
          }
        }
      })
    }).then(function (res) {
      // TEMP DIAGNOSTIC (test theme only): shows whether Klaviyo actually
      // accepted or rejected the request — never assume success from a
      // fetch() that merely didn't throw a network error.
      if (window.console && console.log) {
        console.log("[UWP] Klaviyo subscribe response ←", res.status, res.ok ? "ACCEPTED" : "REJECTED");
      }
      if (!res.ok) {
        var err = new Error("Klaviyo subscribe rejected: HTTP " + res.status);
        err.status = res.status;
        throw err;
      }
      return res;
    });
  }

  // ---------- Submit ----------
  form.addEventListener("submit", function (e) {
    e.preventDefault();
    var email = (emailInput.value || "").trim();
    errorEl.textContent = "";
    emailInput.removeAttribute("aria-invalid");

    if (!isValidEmail(email)) {
      errorEl.textContent = "Please enter a valid email address.";
      emailInput.setAttribute("aria-invalid", "true");
      emailInput.focus();
      return;
    }

    submitBtn.disabled = true;
    submitLabel.textContent = "Joining…";

    klaviyoSubscribe(email)
      .then(function () {
        // Only reached when Klaviyo has confirmed (2xx) the subscription —
        // this is the sole gate on showing the success state.
        writeState({ subscribedAt: Date.now() });
        showSuccess();
      })
      .catch(function (err) {
        // TEMP DIAGNOSTIC (test theme only): safe error summary only —
        // no keys, tokens, or customer data.
        if (window.console && console.warn) {
          console.warn("[UWP] Klaviyo subscribe failed:", err && err.status ? ("HTTP " + err.status) : "network error");
        }
        // Klaviyo did not confirm the subscription. Keep the signup form
        // visible and show a discreet error — never show "Check Your
        // Inbox" for a subscription that was not actually accepted.
        submitBtn.disabled = false;
        submitLabel.textContent = "Join & Receive 10% Off";
        errorEl.textContent = "We couldn't complete your signup. Please try again.";
      });
  });

  function showSuccess() {
    content.setAttribute("data-uwp-state", "success");
    var firstFocusable = overlay.querySelector(".uwp-success-fields " + KEYBOARD_FOCUSABLE);
    if (firstFocusable) firstFocusable.focus();
  }

  shopBtn.addEventListener("click", function () {
    closePopup("shop");
  });
})();
